import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { teacherAPI, videoAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { 
  BookOpen, Video, FileText, Calendar as CalendarIcon, LogOut, Loader2, Home, 
  Plus, Link as LinkIcon, Upload, Play, Clock, Users, Edit, Trash2, 
  CheckCircle, AlertCircle, ExternalLink, VideoIcon, Radio, Globe
} from 'lucide-react';
import JitsiMeet from '../components/JitsiMeet';

const TeacherDashboard = () => {
  const { language, toggleLanguage } = useLanguage();
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal states
  const [zoomModal, setZoomModal] = useState({ open: false, session: null });
  const [recordingModal, setRecordingModal] = useState({ open: false, session: null });
  const [courseModal, setCourseModal] = useState(false);
  const [sessionModal, setSessionModal] = useState(false);
  const [materialModal, setMaterialModal] = useState(false);
  const [courseDetailModal, setCourseDetailModal] = useState({ open: false, course: null });
  const [zoomLink, setZoomLink] = useState('');
  const [recordingFile, setRecordingFile] = useState(null);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Video/Live Class state
  const [liveSession, setLiveSession] = useState(null);
  const [startingLiveClass, setStartingLiveClass] = useState(false);
  
  // Course sessions state
  const [courseSessions, setCourseSessions] = useState([]);
  const [loadingCourseSessions, setLoadingCourseSessions] = useState(false);
  
  // Materials state
  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [materialForm, setMaterialForm] = useState({
    file: null,
    course_id: '',
    title: '',
    title_ar: '',
    session_id: ''
  });
  
  // Course form state
  const [courseForm, setCourseForm] = useState({
    title: '',
    title_ar: '',
    description: '',
    description_ar: '',
    course_type: 'group',
    max_students: 10,
    duration_weeks: 4,
    sessions_per_week: 2
  });
  
  // Session form state
  const [sessionForm, setSessionForm] = useState({
    course_id: '',
    title: '',
    title_ar: '',
    scheduled_date: null,
    scheduled_time: '10:00',
    duration_minutes: 60
  });

  const content = {
    en: {
      title: "Teacher Portal",
      welcome: "Welcome",
      overview: "Overview",
      courses: "My Courses",
      sessions: "Class Sessions",
      materials: "Materials",
      schedule: "Schedule",
      totalCourses: "Total Courses",
      upcomingSessions: "Upcoming Sessions",
      totalMaterials: "Total Materials",
      students: "Enrolled Students",
      noCourses: "You don't have any courses yet.",
      noSessions: "No upcoming sessions",
      noMaterials: "No materials uploaded yet",
      setZoomLink: "Set Zoom Link",
      addRecording: "Upload Recording",
      zoomLinkPlaceholder: "https://zoom.us/j/...",
      selectRecordingFile: "Select Recording File",
      recordingFileTypes: "Allowed: MP4, WebM, MOV, AVI",
      activate: "Activate & Save",
      save: "Save",
      cancel: "Cancel",
      zoomActive: "Zoom Active",
      zoomInactive: "Set Zoom",
      hasRecording: "Has Recording",
      noRecording: "No Recording",
      viewRecording: "View Recording",
      logout: "Logout",
      backToHome: "Back to Home",
      sessionTime: "Session Time",
      duration: "Duration",
      minutes: "minutes",
      courseName: "Course",
      success: "Success!",
      zoomSaved: "Zoom link saved. Students can now join.",
      recordingSaved: "Recording uploaded successfully!",
      createCourse: "Create Course",
      createSession: "Create Session",
      uploadMaterial: "Upload Material",
      // Course form
      courseTitle: "Course Title (English)",
      courseTitleAr: "Course Title (Arabic)",
      courseDescription: "Description (English)",
      courseDescriptionAr: "Description (Arabic)",
      courseType: "Course Type",
      groupCourse: "Group",
      privateCourse: "Private",
      maxStudents: "Max Students",
      durationWeeks: "Duration (weeks)",
      sessionsPerWeek: "Sessions per Week",
      courseCreated: "Course created successfully!",
      viewCourse: "View Course",
      editCourse: "Edit Course",
      weeks: "weeks",
      sessionsWeek: "sessions/week",
      enrolledStudents: "Enrolled Students",
      courseSessions: "Course Sessions",
      addSession: "Add Session",
      noCourseSessions: "No sessions created for this course yet",
      scheduled: "Scheduled",
      completed: "Completed",
      // Live Class
      startLiveClass: "Start Live Class",
      joinLiveClass: "Join Live Class",
      liveNow: "LIVE",
      endingClass: "Ending class...",
      classEnded: "Class ended",
      noPrivateChat: "Private chat is disabled. Group chat only.",
      // Session form
      sessionTitle: "Session Title (English)",
      sessionTitleAr: "Session Title (Arabic)",
      selectCourse: "Select Course",
      selectDate: "Select Date",
      selectTime: "Time",
      durationMinutes: "Duration (minutes)",
      sessionCreated: "Session created successfully!",
      // Material form
      materialTitle: "Material Title (English)",
      materialTitleAr: "Material Title (Arabic)",
      selectFile: "Select File",
      chooseFile: "Choose File",
      selectSession: "Select Session (Optional)",
      materialUploaded: "Material uploaded successfully!",
      fileTypes: "Allowed: PDF, Word, PowerPoint, Video, Images",
      pdf: "PDF",
      document: "Document",
      video: "Video",
      image: "Image"
    },
    ar: {
      title: "بوابة المعلم",
      welcome: "مرحباً",
      overview: "نظرة عامة",
      courses: "دوراتي",
      sessions: "الحصص",
      materials: "المواد",
      schedule: "الجدول",
      totalCourses: "إجمالي الدورات",
      upcomingSessions: "الحصص القادمة",
      totalMaterials: "إجمالي المواد",
      students: "الطلاب المسجلين",
      noCourses: "ليس لديك أي دورات حتى الآن.",
      noSessions: "لا توجد حصص قادمة",
      setZoomLink: "تعيين رابط زوم",
      addRecording: "رفع التسجيل",
      zoomLinkPlaceholder: "https://zoom.us/j/...",
      selectRecordingFile: "اختر ملف التسجيل",
      recordingFileTypes: "المسموح: MP4, WebM, MOV, AVI",
      activate: "تفعيل وحفظ",
      save: "حفظ",
      cancel: "إلغاء",
      zoomActive: "زوم نشط",
      zoomInactive: "تعيين زوم",
      hasRecording: "يوجد تسجيل",
      noRecording: "لا يوجد تسجيل",
      viewRecording: "عرض التسجيل",
      logout: "تسجيل الخروج",
      backToHome: "العودة للرئيسية",
      sessionTime: "وقت الحصة",
      duration: "المدة",
      minutes: "دقيقة",
      courseName: "الدورة",
      success: "تم بنجاح!",
      zoomSaved: "تم حفظ رابط زوم. يمكن للطلاب الانضمام الآن.",
      recordingSaved: "تم حفظ رابط التسجيل.",
      createCourse: "إنشاء دورة",
      createSession: "إنشاء حصة",
      uploadMaterial: "رفع مادة",
      // Course form
      courseTitle: "عنوان الدورة (إنجليزي)",
      courseTitleAr: "عنوان الدورة (عربي)",
      courseDescription: "الوصف (إنجليزي)",
      courseDescriptionAr: "الوصف (عربي)",
      courseType: "نوع الدورة",
      groupCourse: "جماعية",
      privateCourse: "خاصة",
      maxStudents: "الحد الأقصى للطلاب",
      durationWeeks: "المدة (أسابيع)",
      sessionsPerWeek: "الحصص في الأسبوع",
      courseCreated: "تم إنشاء الدورة بنجاح!",
      viewCourse: "عرض الدورة",
      editCourse: "تعديل الدورة",
      weeks: "أسابيع",
      sessionsWeek: "حصص/أسبوع",
      enrolledStudents: "الطلاب المسجلين",
      courseSessions: "حصص الدورة",
      addSession: "إضافة حصة",
      noCourseSessions: "لا توجد حصص لهذه الدورة بعد",
      scheduled: "مجدولة",
      completed: "مكتملة",
      // Live Class
      startLiveClass: "بدء الفصل المباشر",
      joinLiveClass: "انضم للفصل المباشر",
      liveNow: "مباشر",
      endingClass: "جاري إنهاء الفصل...",
      classEnded: "انتهى الفصل",
      noPrivateChat: "الدردشة الخاصة معطلة. الدردشة الجماعية فقط.",
      // Session form
      sessionTitle: "عنوان الحصة (إنجليزي)",
      sessionTitleAr: "عنوان الحصة (عربي)",
      selectCourse: "اختر الدورة",
      selectDate: "اختر التاريخ",
      selectTime: "الوقت",
      durationMinutes: "المدة (دقائق)",
      sessionCreated: "تم إنشاء الحصة بنجاح!",
      // Material form
      materialTitle: "عنوان المادة (إنجليزي)",
      materialTitleAr: "عنوان المادة (عربي)",
      selectFile: "اختر ملف",
      chooseFile: "اختر ملف",
      selectSession: "اختر الحصة (اختياري)",
      materialUploaded: "تم رفع المادة بنجاح!",
      noMaterials: "لا توجد مواد مرفوعة بعد",
      fileTypes: "المسموح: PDF، Word، PowerPoint، فيديو، صور",
      pdf: "PDF",
      document: "مستند",
      video: "فيديو",
      image: "صورة"
    }
  };

  const t = content[language];

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }

    if (!authLoading && user && user.role !== 'teacher') {
      navigate(`/${user.role}`);
      return;
    }

    const fetchDashboard = async () => {
      try {
        const response = await teacherAPI.getDashboard();
        setDashboard(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (user && user.role === 'teacher') {
      fetchDashboard();
    }
  }, [user, authLoading, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSetZoom = async () => {
    if (!zoomLink.trim()) return;
    setSubmitting(true);
    try {
      await teacherAPI.setZoomLink(zoomModal.session.id, zoomLink, true);
      // Refresh dashboard
      const response = await teacherAPI.getDashboard();
      setDashboard(response.data);
      setZoomModal({ open: false, session: null });
      setZoomLink('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save Zoom link');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRecording = async () => {
    if (!recordingFile) return;
    setUploadingRecording(true);
    try {
      await teacherAPI.uploadRecordingFile(recordingModal.session.id, recordingFile);
      // Refresh dashboard
      const response = await teacherAPI.getDashboard();
      setDashboard(response.data);
      
      // Refresh course sessions if modal is open
      if (courseDetailModal.open && courseDetailModal.course) {
        const sessionsRes = await teacherAPI.getSessions({ course_id: courseDetailModal.course.id });
        setCourseSessions(sessionsRes.data.sessions || []);
      }
      
      setRecordingModal({ open: false, session: null });
      setRecordingFile(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload recording');
    } finally {
      setUploadingRecording(false);
    }
  };

  const handleCreateCourse = async () => {
    if (!courseForm.title.trim()) return;
    setSubmitting(true);
    try {
      await teacherAPI.createCourse({
        ...courseForm,
        price: parseFloat(courseForm.price) || 0
      });
      // Refresh dashboard
      const response = await teacherAPI.getDashboard();
      setDashboard(response.data);
      setCourseModal(false);
      setCourseForm({
        title: '',
        title_ar: '',
        description: '',
        description_ar: '',
        course_type: 'group',
        max_students: 10,
        duration_weeks: 4,
        sessions_per_week: 2
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create course');
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch materials
  const fetchMaterials = async () => {
    setLoadingMaterials(true);
    try {
      const response = await teacherAPI.getMaterials();
      setMaterials(response.data.materials || []);
    } catch (err) {
      console.error('Failed to fetch materials:', err);
    } finally {
      setLoadingMaterials(false);
    }
  };

  // Handle material upload
  const handleUploadMaterial = async () => {
    if (!materialForm.file || !materialForm.course_id || !materialForm.title.trim()) return;
    setUploadingMaterial(true);
    try {
      await teacherAPI.uploadMaterialFile(
        materialForm.file,
        materialForm.course_id,
        materialForm.title,
        materialForm.title_ar || null,
        materialForm.session_id || null
      );
      // Refresh data
      const response = await teacherAPI.getDashboard();
      setDashboard(response.data);
      fetchMaterials();
      setMaterialModal(false);
      setMaterialForm({
        file: null,
        course_id: '',
        title: '',
        title_ar: '',
        session_id: ''
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload material');
    } finally {
      setUploadingMaterial(false);
    }
  };

  // Open course detail modal and fetch sessions
  const openCourseDetail = async (course) => {
    setCourseDetailModal({ open: true, course });
    setLoadingCourseSessions(true);
    try {
      const response = await teacherAPI.getSessions({ course_id: course.id });
      setCourseSessions(response.data.sessions || []);
    } catch (err) {
      console.error('Failed to fetch course sessions:', err);
      setCourseSessions([]);
    } finally {
      setLoadingCourseSessions(false);
    }
  };

  // Add session for a specific course
  const openSessionModalForCourse = (courseId) => {
    setSessionForm(prev => ({ ...prev, course_id: courseId }));
    setSessionModal(true);
  };

  const handleCreateSession = async () => {
    if (!sessionForm.title.trim() || !sessionForm.course_id || !sessionForm.scheduled_date) return;
    setSubmitting(true);
    try {
      // Combine date and time
      const scheduledDateTime = new Date(sessionForm.scheduled_date);
      const [hours, minutes] = sessionForm.scheduled_time.split(':');
      scheduledDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      await teacherAPI.createSession({
        course_id: sessionForm.course_id,
        title: sessionForm.title,
        title_ar: sessionForm.title_ar,
        scheduled_time: scheduledDateTime.toISOString(),
        duration_minutes: sessionForm.duration_minutes
      });
      // Refresh dashboard
      const response = await teacherAPI.getDashboard();
      setDashboard(response.data);
      setSessionModal(false);
      
      // Refresh course sessions if modal is open
      if (courseDetailModal.open && courseDetailModal.course) {
        const sessionsRes = await teacherAPI.getSessions({ course_id: courseDetailModal.course.id });
        setCourseSessions(sessionsRes.data.sessions || []);
      }
      
      setSessionForm({
        course_id: '',
        title: '',
        title_ar: '',
        scheduled_date: null,
        scheduled_time: '10:00',
        duration_minutes: 60
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create session');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Course
  const handleDeleteCourse = async (courseId, courseName) => {
    if (!window.confirm(
      language === 'ar' 
        ? `هل أنت متأكد من حذف الدورة "${courseName}"؟ سيتم حذف جميع الجلسات والمواد المرتبطة بها.`
        : `Are you sure you want to delete the course "${courseName}"? All associated sessions and materials will also be deleted.`
    )) {
      return;
    }
    
    try {
      await teacherAPI.deleteCourse(courseId);
      // Refresh dashboard
      const response = await teacherAPI.getDashboard();
      setDashboard(response.data);
      // Close course detail modal if open
      if (courseDetailModal.open && courseDetailModal.course?.id === courseId) {
        setCourseDetailModal({ open: false, course: null });
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete course');
    }
  };

  // Delete Session
  const handleDeleteSession = async (sessionId, sessionTitle) => {
    if (!window.confirm(
      language === 'ar' 
        ? `هل أنت متأكد من حذف الجلسة "${sessionTitle}"؟`
        : `Are you sure you want to delete the session "${sessionTitle}"?`
    )) {
      return;
    }
    
    try {
      await teacherAPI.deleteSession(sessionId);
      // Refresh dashboard
      const response = await teacherAPI.getDashboard();
      setDashboard(response.data);
      // Refresh course sessions if modal is open
      if (courseDetailModal.open && courseDetailModal.course) {
        const sessionsResponse = await teacherAPI.getSessions({ course_id: courseDetailModal.course.id });
        setCourseSessions(sessionsResponse.data.sessions || []);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete session');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Start Live Class
  const handleStartLiveClass = async (sessionId, sessionTitle) => {
    setStartingLiveClass(true);
    try {
      const response = await videoAPI.startSession(sessionId);
      setLiveSession({
        ...response.data,
        sessionId,
        sessionTitle: sessionTitle || 'Live Class'
      });
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start live class');
    } finally {
      setStartingLiveClass(false);
    }
  };

  // End Live Class
  const handleEndLiveClass = async () => {
    if (!liveSession?.video_session_id) {
      setLiveSession(null);
      return;
    }
    
    try {
      await videoAPI.endSession(liveSession.video_session_id);
      setLiveSession(null);
      // Refresh dashboard to update session status
      const response = await teacherAPI.getDashboard();
      setDashboard(response.data);
    } catch (err) {
      console.error('Failed to end class:', err);
      setLiveSession(null);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/login')}>Go to Login</Button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: t.overview, icon: BookOpen },
    { id: 'courses', label: t.courses, icon: BookOpen },
    { id: 'sessions', label: t.sessions, icon: Video },
    { id: 'materials', label: t.materials, icon: FileText },
    { id: 'schedule', label: t.schedule, icon: CalendarIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {dashboard?.teacher?.profile_image && (
              <img 
                src={dashboard.teacher.profile_image} 
                alt={dashboard.teacher.name}
                className="w-12 h-12 rounded-full object-cover"
              />
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
              <p className="text-gray-600">{t.welcome}, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={toggleLanguage}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              <span className="hidden sm:inline">{language === 'en' ? 'العربية' : 'English'}</span>
            </Button>
            <Link to="/" className="text-gray-600 hover:text-teal-600 flex items-center gap-2">
              <Home className="w-5 h-5" />
              <span className="hidden sm:inline">{t.backToHome}</span>
            </Link>
            <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t.logout}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Tabs Navigation */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex gap-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'border-teal-600 text-teal-600' 
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <BookOpen className="w-8 h-8 text-teal-600 mb-3" />
                <h3 className="text-3xl font-bold text-gray-900">{dashboard?.total_courses || 0}</h3>
                <p className="text-gray-600">{t.totalCourses}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <CalendarIcon className="w-8 h-8 text-blue-600 mb-3" />
                <h3 className="text-3xl font-bold text-gray-900">{dashboard?.upcoming_sessions?.length || 0}</h3>
                <p className="text-gray-600">{t.upcomingSessions}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <FileText className="w-8 h-8 text-purple-600 mb-3" />
                <h3 className="text-3xl font-bold text-gray-900">{dashboard?.total_materials || 0}</h3>
                <p className="text-gray-600">{t.totalMaterials}</p>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <Users className="w-8 h-8 text-amber-600 mb-3" />
                <h3 className="text-3xl font-bold text-gray-900">-</h3>
                <p className="text-gray-600">{t.students}</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => setCourseModal(true)} className="bg-teal-600 hover:bg-teal-700">
                  <Plus className="w-4 h-4 mr-2" />
                  {t.createCourse}
                </Button>
                <Button onClick={() => setSessionModal(true)} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  {t.createSession}
                </Button>
                <Button variant="outline">
                  <Upload className="w-4 h-4 mr-2" />
                  {t.uploadMaterial}
                </Button>
              </div>
            </div>

            {/* Upcoming Sessions */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t.upcomingSessions}</h2>
              {dashboard?.upcoming_sessions?.length > 0 ? (
                <div className="space-y-4">
                  {dashboard.upcoming_sessions.slice(0, 5).map((session) => (
                    <div key={session.id} className="border rounded-lg p-4 hover:border-teal-300 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{session.title}</h3>
                          <p className="text-sm text-gray-600 mt-1">
                            <Clock className="w-4 h-4 inline mr-1" />
                            {formatDateTime(session.scheduled_time)}
                          </p>
                          <p className="text-sm text-gray-500">
                            {t.duration}: {session.duration_minutes} {t.minutes}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {session.recording_url ? (
                            <a
                              href={session.recording_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-purple-600 text-sm bg-purple-50 px-3 py-1 rounded-full hover:bg-purple-100"
                            >
                              <Play className="w-4 h-4" />
                              {t.viewRecording}
                            </a>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setRecordingModal({ open: true, session })}
                              className="text-purple-600 border-purple-200 hover:bg-purple-50"
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              {t.addRecording}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t.noSessions}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-6 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t.courses}</h2>
                <p className="text-sm text-gray-600">{dashboard?.courses?.length || 0} {t.totalCourses}</p>
              </div>
              <Button 
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => setCourseModal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t.createCourse}
              </Button>
            </div>
            <div className="p-6">
              {dashboard?.courses?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dashboard.courses.map((course) => (
                    <div 
                      key={course.id} 
                      className="border rounded-lg p-5 hover:border-teal-300 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {language === 'ar' ? (course.title_ar || course.title) : course.title}
                          </h3>
                          {course.title_ar && language === 'en' && (
                            <p className="text-sm text-gray-500 truncate">{course.title_ar}</p>
                          )}
                          {course.title && language === 'ar' && course.title_ar && (
                            <p className="text-sm text-gray-500 truncate">{course.title}</p>
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          course.course_type === 'group' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {course.course_type === 'group' ? t.groupCourse : t.privateCourse}
                        </span>
                      </div>
                      
                      {(course.description || course.description_ar) && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {language === 'ar' ? (course.description_ar || course.description) : course.description}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap gap-2 text-xs text-gray-500 mb-3">
                        <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                          <Users className="w-3 h-3" />
                          {course.max_students} {t.maxStudents}
                        </span>
                        <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                          <CalendarIcon className="w-3 h-3" />
                          {course.duration_weeks} {t.weeks}
                        </span>
                        <span className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded">
                          <Clock className="w-3 h-3" />
                          {course.sessions_per_week} {t.sessionsWeek}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between pt-3 border-t gap-1">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openCourseDetail(course)}
                          className="text-gray-600"
                        >
                          <Video className="w-3 h-3 mr-1" />
                          {t.viewCourse}
                        </Button>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-teal-600 hover:text-teal-700"
                            onClick={() => openSessionModalForCourse(course.id)}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            {t.addSession}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteCourse(course.id, course.title || course.title_ar)}
                            data-testid={`delete-course-${course.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t.noCourses}</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setCourseModal(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t.createCourse}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">{t.sessions}</h2>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => setSessionModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                {t.createSession}
              </Button>
            </div>
            
            {dashboard?.upcoming_sessions?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Title</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">{t.sessionTime}</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">{t.duration}</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Recording</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {dashboard.upcoming_sessions.map((session) => (
                      <tr key={session.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">{session.title}</span>
                          {session.title_ar && <span className="block text-sm text-gray-500">{session.title_ar}</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDateTime(session.scheduled_time)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {session.duration_minutes} {t.minutes}
                        </td>
                        <td className="px-4 py-3">
                          {session.recording_url ? (
                            <a href={session.recording_url} target="_blank" rel="noopener noreferrer" className="text-purple-600 text-xs hover:underline flex items-center gap-1">
                              <ExternalLink className="w-3 h-3" />
                              View
                            </a>
                          ) : (
                            <span className="text-gray-400 text-xs">None</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 flex-wrap">
                            {/* Start Live Class Button - Embedded with Auto-Recording */}
                            <Button 
                              size="sm" 
                              variant={session.is_live ? "default" : "outline"}
                              className={session.is_live ? "bg-red-500 hover:bg-red-600 text-white" : "text-teal-600 border-teal-300 hover:bg-teal-50"}
                              onClick={() => handleStartLiveClass(session.id, session.title || session.title_ar)}
                              disabled={startingLiveClass}
                              data-testid={`start-live-${session.id}`}
                              title="Start class with auto-recording"
                            >
                              {startingLiveClass ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : session.is_live ? (
                                <>
                                  <Radio className="w-4 h-4 mr-1 animate-pulse" />
                                  {t.liveNow}
                                </>
                              ) : (
                                <>
                                  <VideoIcon className="w-4 h-4 mr-1" />
                                  {t.startLiveClass}
                                </>
                              )}
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setRecordingModal({ open: true, session })}
                            >
                              <Upload className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteSession(session.id, session.title || session.title_ar)}
                              data-testid={`delete-session-table-${session.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Video className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>{t.noSessions}</p>
              </div>
            )}
          </div>
        )}

        {/* Materials Tab */}
        {activeTab === 'materials' && (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-6 border-b bg-gray-50 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{t.materials}</h2>
                <p className="text-sm text-gray-600">{t.fileTypes}</p>
              </div>
              <Button 
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => {
                  setMaterialModal(true);
                  fetchMaterials();
                }}
              >
                <Upload className="w-4 h-4 mr-2" />
                {t.uploadMaterial}
              </Button>
            </div>
            <div className="p-6">
              {loadingMaterials ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-600" />
                </div>
              ) : materials.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {materials.map((material) => (
                    <div 
                      key={material.id} 
                      className="border rounded-lg p-4 hover:border-teal-300 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          material.material_type === 'video' ? 'bg-purple-100 text-purple-600' :
                          material.material_type === 'pdf' ? 'bg-red-100 text-red-600' :
                          material.material_type === 'image' ? 'bg-blue-100 text-blue-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {material.material_type === 'video' ? <Video className="w-5 h-5" /> :
                           material.material_type === 'pdf' ? <FileText className="w-5 h-5" /> :
                           material.material_type === 'image' ? <FileText className="w-5 h-5" /> :
                           <FileText className="w-5 h-5" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-gray-900 truncate">{material.title}</h3>
                          {material.title_ar && <p className="text-sm text-gray-500 truncate">{material.title_ar}</p>}
                          <p className="text-xs text-gray-400 mt-1">
                            {material.file_name} • {(material.file_size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          material.material_type === 'video' ? 'bg-purple-100 text-purple-700' :
                          material.material_type === 'pdf' ? 'bg-red-100 text-red-700' :
                          material.material_type === 'image' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {t[material.material_type] || material.material_type}
                        </span>
                        <a 
                          href={`${process.env.REACT_APP_BACKEND_URL}${material.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-600 hover:text-teal-700 text-sm flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          {language === 'ar' ? 'عرض' : 'View'}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t.noMaterials}</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setMaterialModal(true)}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {t.uploadMaterial}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-6 border-b bg-gray-50">
              <h2 className="text-lg font-semibold text-gray-900">{t.schedule}</h2>
              <p className="text-sm text-gray-600 mt-1">{t.upcomingSessions}</p>
            </div>
            <div className="p-6">
              {dashboard?.upcoming_sessions?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.upcoming_sessions.map((session) => (
                    <div 
                      key={session.id} 
                      className={`border rounded-lg p-4 transition-colors ${session.zoom_link_active ? 'border-green-300 bg-green-50/50' : 'border-gray-200 hover:border-teal-300'}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 truncate">{session.title}</h3>
                            {session.course?.title && (
                              <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                                {language === 'ar' ? session.course.title_ar || session.course.title : session.course.title}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="w-4 h-4" />
                              {formatDateTime(session.scheduled_time)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {session.duration_minutes} {t.minutes}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {session.is_live && (
                            <span className="flex items-center gap-1 text-red-600 text-xs bg-red-100 px-2 py-1 rounded-full animate-pulse">
                              <Radio className="w-3 h-3" />
                              LIVE
                            </span>
                          )}
                          {session.recording_url && (
                            <a 
                              href={session.recording_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-purple-600 text-xs bg-purple-100 px-2 py-1 rounded-full"
                            >
                              <Play className="w-3 h-3" />
                              {t.hasRecording}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{t.noSessions}</p>
                  <Button 
                    onClick={() => setSessionModal(true)} 
                    variant="outline" 
                    className="mt-4"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t.createSession}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Recording Upload Modal */}
      <Dialog open={recordingModal.open} onOpenChange={(open) => !open && setRecordingModal({ open: false, session: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-purple-600" />
              {t.addRecording}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <Label className="text-xs text-gray-500">{t.sessions}</Label>
              <p className="font-medium text-gray-900">{recordingModal.session?.title}</p>
              {recordingModal.session?.scheduled_time && (
                <p className="text-sm text-gray-600">{formatDateTime(recordingModal.session.scheduled_time)}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>{t.selectRecordingFile}</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
                <input
                  type="file"
                  accept=".mp4,.webm,.mov,.avi,.mpeg,video/*"
                  onChange={(e) => setRecordingFile(e.target.files[0])}
                  className="hidden"
                  id="recording-file-input"
                />
                <label 
                  htmlFor="recording-file-input"
                  className="cursor-pointer"
                >
                  {recordingFile ? (
                    <div>
                      <Video className="w-12 h-12 mx-auto text-purple-600 mb-2" />
                      <p className="font-medium text-gray-900">{recordingFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(recordingFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <p className="text-xs text-purple-600 mt-2">{language === 'ar' ? 'تغيير الملف' : 'Change file'}</p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600">{language === 'ar' ? 'اختر ملف فيديو' : 'Choose video file'}</p>
                      <p className="text-xs text-gray-400 mt-1">{t.recordingFileTypes}</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRecordingModal({ open: false, session: null }); setRecordingFile(null); }}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleAddRecording} 
              disabled={uploadingRecording || !recordingFile}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {uploadingRecording ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <Upload className="w-4 h-4 mr-1" />
                  {t.addRecording}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Course Modal */}
      <Dialog open={courseModal} onOpenChange={setCourseModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.createCourse}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.courseTitle}</Label>
                <Input
                  value={courseForm.title}
                  onChange={(e) => setCourseForm({...courseForm, title: e.target.value})}
                  placeholder="English Conversation"
                />
              </div>
              <div className="space-y-2">
                <Label>{t.courseTitleAr}</Label>
                <Input
                  value={courseForm.title_ar}
                  onChange={(e) => setCourseForm({...courseForm, title_ar: e.target.value})}
                  placeholder="محادثة إنجليزية"
                  dir="rtl"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t.courseDescription}</Label>
              <Textarea
                value={courseForm.description}
                onChange={(e) => setCourseForm({...courseForm, description: e.target.value})}
                placeholder="Course description..."
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t.courseDescriptionAr}</Label>
              <Textarea
                value={courseForm.description_ar}
                onChange={(e) => setCourseForm({...courseForm, description_ar: e.target.value})}
                placeholder="وصف الدورة..."
                rows={3}
                dir="rtl"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.courseType}</Label>
                <select
                  value={courseForm.course_type}
                  onChange={(e) => setCourseForm({...courseForm, course_type: e.target.value})}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  <option value="group">{t.groupCourse}</option>
                  <option value="private">{t.privateCourse}</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>{t.maxStudents}</Label>
                <Input
                  type="number"
                  value={courseForm.max_students}
                  onChange={(e) => setCourseForm({...courseForm, max_students: parseInt(e.target.value) || 1})}
                  min={1}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.durationWeeks}</Label>
                <Input
                  type="number"
                  value={courseForm.duration_weeks}
                  onChange={(e) => setCourseForm({...courseForm, duration_weeks: parseInt(e.target.value) || 1})}
                  min={1}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.sessionsPerWeek}</Label>
                <Input
                  type="number"
                  value={courseForm.sessions_per_week}
                  onChange={(e) => setCourseForm({...courseForm, sessions_per_week: parseInt(e.target.value) || 1})}
                  min={1}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCourseModal(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleCreateCourse} 
              disabled={submitting || !courseForm.title.trim()}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t.createCourse}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Session Modal */}
      <Dialog open={sessionModal} onOpenChange={setSessionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.createSession}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t.selectCourse}</Label>
              <select
                value={sessionForm.course_id}
                onChange={(e) => setSessionForm({...sessionForm, course_id: e.target.value})}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">{t.selectCourse}...</option>
                {dashboard?.courses?.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title || course.title_ar}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.sessionTitle}</Label>
                <Input
                  value={sessionForm.title}
                  onChange={(e) => setSessionForm({...sessionForm, title: e.target.value})}
                  placeholder="Lesson 1: Introduction"
                />
              </div>
              <div className="space-y-2">
                <Label>{t.sessionTitleAr}</Label>
                <Input
                  value={sessionForm.title_ar}
                  onChange={(e) => setSessionForm({...sessionForm, title_ar: e.target.value})}
                  placeholder="الدرس 1: المقدمة"
                  dir="rtl"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.selectDate}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${!sessionForm.scheduled_date && 'text-muted-foreground'}`}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {sessionForm.scheduled_date ? format(sessionForm.scheduled_date, 'PPP') : t.selectDate}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={sessionForm.scheduled_date}
                      onSelect={(date) => setSessionForm({...sessionForm, scheduled_date: date})}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>{t.selectTime}</Label>
                <Input
                  type="time"
                  value={sessionForm.scheduled_time}
                  onChange={(e) => setSessionForm({...sessionForm, scheduled_time: e.target.value})}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t.durationMinutes}</Label>
              <select
                value={sessionForm.duration_minutes}
                onChange={(e) => setSessionForm({...sessionForm, duration_minutes: parseInt(e.target.value)})}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value={30}>30 {t.minutes}</option>
                <option value={45}>45 {t.minutes}</option>
                <option value={60}>60 {t.minutes}</option>
                <option value={90}>90 {t.minutes}</option>
                <option value={120}>120 {t.minutes}</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSessionModal(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleCreateSession} 
              disabled={submitting || !sessionForm.title.trim() || !sessionForm.course_id || !sessionForm.scheduled_date}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t.createSession}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Material Modal */}
      <Dialog open={materialModal} onOpenChange={setMaterialModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t.uploadMaterial}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t.selectCourse} *</Label>
              <select
                value={materialForm.course_id}
                onChange={(e) => setMaterialForm({...materialForm, course_id: e.target.value})}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">{t.selectCourse}...</option>
                {dashboard?.courses?.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title || course.title_ar}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t.materialTitle} *</Label>
                <Input
                  value={materialForm.title}
                  onChange={(e) => setMaterialForm({...materialForm, title: e.target.value})}
                  placeholder="Chapter 1 Notes"
                />
              </div>
              <div className="space-y-2">
                <Label>{t.materialTitleAr}</Label>
                <Input
                  value={materialForm.title_ar}
                  onChange={(e) => setMaterialForm({...materialForm, title_ar: e.target.value})}
                  placeholder="ملاحظات الفصل 1"
                  dir="rtl"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t.selectFile} *</Label>
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-teal-400 transition-colors">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.mp4,.webm,.mov,.jpg,.jpeg,.png,.gif"
                  onChange={(e) => setMaterialForm({...materialForm, file: e.target.files[0]})}
                  className="hidden"
                  id="material-file-input"
                />
                <label 
                  htmlFor="material-file-input"
                  className="cursor-pointer"
                >
                  {materialForm.file ? (
                    <div>
                      <FileText className="w-10 h-10 mx-auto text-teal-600 mb-2" />
                      <p className="font-medium text-gray-900">{materialForm.file.name}</p>
                      <p className="text-sm text-gray-500">
                        {(materialForm.file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <p className="text-xs text-teal-600 mt-2">{t.chooseFile}</p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                      <p className="text-gray-600">{t.chooseFile}</p>
                      <p className="text-xs text-gray-400 mt-1">{t.fileTypes}</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMaterialModal(false)}>
              {t.cancel}
            </Button>
            <Button 
              onClick={handleUploadMaterial} 
              disabled={uploadingMaterial || !materialForm.file || !materialForm.course_id || !materialForm.title.trim()}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {uploadingMaterial ? <Loader2 className="w-4 h-4 animate-spin" /> : t.uploadMaterial}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Course Detail Modal */}
      <Dialog open={courseDetailModal.open} onOpenChange={(open) => !open && setCourseDetailModal({ open: false, course: null })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-teal-600" />
              {courseDetailModal.course && (language === 'ar' 
                ? (courseDetailModal.course.title_ar || courseDetailModal.course.title) 
                : courseDetailModal.course.title)}
            </DialogTitle>
          </DialogHeader>
          
          {courseDetailModal.course && (
            <div className="space-y-6 py-4">
              {/* Course Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex flex-wrap gap-3 text-sm">
                  <span className={`px-3 py-1 rounded-full font-medium ${
                    courseDetailModal.course.course_type === 'group' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                    {courseDetailModal.course.course_type === 'group' ? t.groupCourse : t.privateCourse}
                  </span>
                  <span className="flex items-center gap-1 bg-white px-3 py-1 rounded-full border">
                    <Users className="w-4 h-4" />
                    {courseDetailModal.course.max_students} {t.maxStudents}
                  </span>
                  <span className="flex items-center gap-1 bg-white px-3 py-1 rounded-full border">
                    <CalendarIcon className="w-4 h-4" />
                    {courseDetailModal.course.duration_weeks} {t.weeks}
                  </span>
                </div>
                
                {(courseDetailModal.course.description || courseDetailModal.course.description_ar) && (
                  <p className="text-gray-600 mt-3">
                    {language === 'ar' 
                      ? (courseDetailModal.course.description_ar || courseDetailModal.course.description) 
                      : courseDetailModal.course.description}
                  </p>
                )}
              </div>
              
              {/* Sessions Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Video className="w-4 h-4 text-teal-600" />
                    {t.courseSessions}
                  </h3>
                  <Button 
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-700"
                    onClick={() => {
                      openSessionModalForCourse(courseDetailModal.course.id);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t.addSession}
                  </Button>
                </div>
                
                {loadingCourseSessions ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-600" />
                  </div>
                ) : courseSessions.length > 0 ? (
                  <div className="space-y-3">
                    {courseSessions.map((session) => {
                      const sessionDate = new Date(session.scheduled_time);
                      const isPast = sessionDate < new Date();
                      
                      return (
                        <div 
                          key={session.id}
                          className={`border rounded-lg p-4 transition-colors ${
                            isPast ? 'bg-gray-50 border-gray-200' : 'hover:border-teal-300'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-gray-900 truncate">
                                  {language === 'ar' ? (session.title_ar || session.title) : session.title}
                                </h4>
                                {isPast ? (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                                    {t.completed}
                                  </span>
                                ) : (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                    {t.scheduled}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <CalendarIcon className="w-4 h-4" />
                                  {formatDateTime(session.scheduled_time)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {session.duration_minutes} {t.minutes}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {session.zoom_link_active ? (
                                <span className="flex items-center gap-1 text-green-600 text-xs bg-green-100 px-2 py-1 rounded-full">
                                  <CheckCircle className="w-3 h-3" />
                                  {t.zoomActive}
                                </span>
                              ) : !isPast && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setZoomLink(session.zoom_link || '');
                                    setZoomModal({ open: true, session });
                                  }}
                                  className="text-xs"
                                >
                                  <LinkIcon className="w-3 h-3 mr-1" />
                                  {t.setZoomLink}
                                </Button>
                              )}
                              {session.recording_url && (
                                <a 
                                  href={session.recording_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-purple-600 text-xs bg-purple-100 px-2 py-1 rounded-full"
                                >
                                  <Play className="w-3 h-3" />
                                  {t.hasRecording}
                                </a>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleDeleteSession(session.id, session.title || session.title_ar)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                data-testid={`delete-session-${session.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                    <Video className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>{t.noCourseSessions}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Jitsi Live Class Modal */}
      {liveSession && (
        <JitsiMeet
          roomName={liveSession.room_name}
          domain={liveSession.jitsi_domain}
          config={liveSession.config}
          userInfo={{
            name: dashboard?.teacher?.name || user?.name || 'Teacher',
            email: user?.email
          }}
          isTeacher={true}
          sessionTitle={liveSession.sessionTitle}
          sessionId={liveSession.sessionId}
          onClose={handleEndLiveClass}
          onRecordingUploaded={(data) => {
            console.log('Recording uploaded:', data);
            // Refresh dashboard to show the new recording
            teacherAPI.getDashboard().then(response => setDashboard(response.data));
          }}
        />
      )}
    </div>
  );
};

export default TeacherDashboard;
